// A phone paired to one deck by scanning the host controller's QR (COP-17).
//
// The signed-in host's laptop asks the backend for a pairing token and puts it
// in the QR as `/host/deck/{deckId}#pair=<token>`. The phone opens straight into
// the controller for that deck with no Google sign-in. The token stands for the
// host's account on THAT deck only, for a day — the backend checks the deck on
// every use, and it is never accepted as an account session (no dashboard,
// billing, history or support chat).
//
// The fragment, not the query string: a fragment never reaches a server log,
// a proxy, or a Referer header. It is read once and wiped from the address bar,
// so it is not left on screen for a screenshot or in the tab's history.

import { getStoredHostSession } from "./webAuth";

const API_URL = process.env.REACT_APP_API_URL ?? "";

const FRAGMENT_KEY = "pair";
const STORAGE_PREFIX = "copresentDevicePairing:";

// The token's own expiry, read from its payload. Unverified on purpose: this
// only decides whether to bother presenting it — the backend is the authority.
const expiryOf = (token: string): number | null => {
  const parts = token.split(".");
  if (parts.length !== 3 || parts[0] !== "pd1") return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(window.atob(padded)) as { Exp?: number };
    return typeof claims.Exp === "number" ? claims.Exp * 1000 : null;
  } catch {
    return null;
  }
};

const storageKey = (deckId: string) => `${STORAGE_PREFIX}${deckId}`;

/**
 * Take a pairing token off the URL fragment, if one is there, store it for this
 * deck, and strip it from the address bar. Call once on load of the host route.
 *
 * Returns true when a pairing was captured.
 */
export const captureDevicePairing = (deckId: string): boolean => {
  if (typeof window === "undefined" || !deckId) return false;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return false;
  const params = new URLSearchParams(hash);
  const token = params.get(FRAGMENT_KEY);
  if (!token) return false;

  // Wipe it first, whatever happens next — a token that failed to store is
  // still not something to leave in the address bar.
  params.delete(FRAGMENT_KEY);
  const rest = params.toString();
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ""}`
    );
  } catch {
    /* ignore */
  }

  const exp = expiryOf(token);
  if (exp === null || exp <= Date.now()) return false;
  try {
    window.localStorage.setItem(storageKey(deckId), token);
  } catch {
    return false;
  }
  return true;
};

/** This device's pairing for a deck, if it has a live one. */
export const getDevicePairing = (deckId: string | undefined): string | null => {
  if (typeof window === "undefined" || !deckId) return null;
  let token: string | null = null;
  try {
    token = window.localStorage.getItem(storageKey(deckId));
  } catch {
    return null;
  }
  if (!token) return null;
  const exp = expiryOf(token);
  // An expired pairing is not presented, but it is kept: it is how the next
  // load knows to say "this Clicker has expired" rather than ask a phone that
  // never signed in to sign in. Rescanning overwrites it.
  if (exp === null || exp <= Date.now()) return null;
  return token;
};

/** True when this device was paired to the deck and that pairing has run out. */
export const hasExpiredDevicePairing = (deckId: string | undefined): boolean => {
  if (typeof window === "undefined" || !deckId) return false;
  let token: string | null = null;
  try {
    token = window.localStorage.getItem(storageKey(deckId));
  } catch {
    return false;
  }
  if (!token) return false;
  const exp = expiryOf(token);
  return exp !== null && exp <= Date.now();
};

/**
 * Replace this device's pairing with the renewed one the backend sent on a JOIN
 * reply (sliding renewal). Ignored unless it is a live pairing.
 */
export const storeDevicePairing = (deckId: string | undefined, token: string): void => {
  if (typeof window === "undefined" || !deckId) return;
  const exp = expiryOf(token);
  if (exp === null || exp <= Date.now()) return;
  try {
    window.localStorage.setItem(storageKey(deckId), token);
  } catch {
    /* ignore */
  }
};

export const clearDevicePairing = (deckId: string | undefined): void => {
  if (typeof window === "undefined" || !deckId) return;
  try {
    window.localStorage.removeItem(storageKey(deckId));
  } catch {
    /* ignore */
  }
};

/**
 * The bearer a host request for this deck should carry: the account when this
 * device is signed in, otherwise its pairing for the deck. The account wins —
 * it is the stronger, wider credential, and a signed-in host who also scanned a
 * QR should not be demoted to a deck-only clicker.
 */
export const hostBearerFor = (deckId: string): string | null =>
  getStoredHostSession()?.sessionToken ?? getDevicePairing(deckId);

/** Whether this device is driving the deck on a pairing rather than an account. */
export const isPairedDevice = (deckId: string | undefined): boolean =>
  !!deckId && !getStoredHostSession() && !!getDevicePairing(deckId);

/**
 * Ask the backend for a pairing token for this deck, as the signed-in host.
 * Null when there is no account here, no live session for the deck yet, or the
 * request failed — the QR then falls back to the plain controller URL, which
 * still works for a host who signs in on the phone.
 */
export const requestDevicePairing = async (
  deckId: string
): Promise<{ token: string; expiresAt: number } | null> => {
  const session = getStoredHostSession();
  if (!session || !deckId) return null;
  try {
    const resp = await fetch(
      `${API_URL}/host/deck/${encodeURIComponent(deckId)}/device`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${session.sessionToken}` },
      }
    );
    if (!resp.ok) return null;
    const body = (await resp.json()) as { deviceToken?: string };
    const token = body.deviceToken;
    const expiresAt = token ? expiryOf(token) : null;
    return token && expiresAt ? { token, expiresAt } : null;
  } catch {
    return null;
  }
};

/** The URL the "Control from your phone" QR encodes. */
export const pairedHostUrl = (
  origin: string,
  deckId: string,
  token: string | null
): string =>
  token
    ? `${origin}/host/deck/${deckId}#${FRAGMENT_KEY}=${encodeURIComponent(token)}`
    : `${origin}/host/deck/${deckId}`;
